import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { assetsApi } from '../../services/assets';
import { employeesApi } from '../../services/employees';
import SearchableSelect from '../../components/SearchableSelect';

function AssetDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasRole, user } = useAuth();
  
  const [asset, setAsset] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const fetchAsset = useCallback(async () => {
    try {
      setLoading(true);
      const response = await assetsApi.getById(id);
      const data = response.data || response;
      console.log('Asset detail data:', data);
      setAsset(data);
    } catch (err) {
      setError('Failed to load asset details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeesApi.getAll();
      const employeesData = response.data || response;
      setEmployees(employeesData.filter(e => e.status === 'active'));
    } catch (err) {
      console.error('Failed to load employees');
    }
  }, []);

  useEffect(() => {
    fetchAsset();
    if (hasRole('super_admin', 'admin_head', 'hr_head')) {
      fetchEmployees();
    }
  }, [fetchAsset, fetchEmployees, hasRole]);

  useEffect(() => {
    if (assigning && hasRole('super_admin', 'admin_head', 'hr_head')) {
      fetchEmployees();
    }
  }, [assigning, fetchEmployees, hasRole]);

  const handleAssign = async () => {
    if (!selectedEmployee) return;
    try {
      await assetsApi.assign(id, selectedEmployee);
      setAssigning(false);
      setSelectedEmployee('');
      fetchAsset();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign asset');
    }
  };

  const handleReturn = async () => {
    try {
      await assetsApi.return(id);
      fetchAsset();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to return asset');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await assetsApi.delete(id);
      navigate('/assets');
    } catch (err) {
      setError('Failed to delete asset');
    }
  };

  if (loading) return <div className="loading">Loading asset details...</div>;
  if (!asset) return <div className="alert alert-error">Asset not found</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Asset Details</h2>
        <div className="page-actions">
          {(hasRole('super_admin', 'admin_head', 'hr_head') || (hasRole('employee') && asset.assigned_to === user?.employeeId)) && (
            <>
              <button className="btn btn-secondary btn-compact" onClick={() => navigate(`/assets/${id}/edit`)}>
                Edit
              </button>
            </>
          )}
          {hasRole('super_admin', 'admin_head', 'hr_head') && (
            <>
              {asset.status === 'assigned' ? (
                <button className="btn btn-warning btn-compact" onClick={handleReturn}>
                  Return Asset
                </button>
              ) : (
                <button className="btn btn-primary btn-compact" onClick={() => setAssigning(true)}>
                  Assign to Employee
                </button>
              )}
              {hasRole('super_admin', 'admin_head') && (
                <button className="btn btn-danger btn-compact" onClick={handleDelete}>
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {assigning && (
        <div className="card assign-card">
          <h3 style={{ marginBottom: '16px' }}>Assign Asset to Employee</h3>
          <div className="form-row">
            <div className="form-group">
              <SearchableSelect
                options={employees}
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                placeholder="Search employees by name or email..."
                displayField="name"
                subDisplayField="email"
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary btn-compact" onClick={() => setAssigning(false)}>
              Cancel
            </button>
            <button className="btn btn-primary btn-compact" onClick={handleAssign} disabled={!selectedEmployee}>
              Assign
            </button>
          </div>
        </div>
      )}

      <div className="detail-grid">
        <div className="card detail-card">
          <h3>Basic Information</h3>
          <div className="detail-row">
            <span className="detail-label">Asset Type:</span>
            <span className="detail-value">{asset.asset_type}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Make / Model:</span>
            <span className="detail-value">
              {asset.make || asset.model ? `${asset.make || ''} ${asset.model || ''}`.trim() : 'N/A'}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Serial Number:</span>
            <span className="detail-value">{asset.serial_number || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Status:</span>
            <span className={`detail-value status-${asset.status}`}>{asset.status}</span>
          </div>
          {asset.status === 'company' && (
            <div className="detail-row">
              <span className="detail-label">Location:</span>
              <span className="detail-value">{asset.location || 'N/A'}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Purchase Date:</span>
            <span className="detail-value">{asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString() : 'N/A'}</span>
          </div>
        </div>

        <div className="card detail-card">
          <h3>System Information</h3>
          <div className="detail-row">
            <span className="detail-label">Operating System:</span>
            <span className="detail-value">{asset.os} {asset.os_version}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Last Patch Date:</span>
            <span className="detail-value">
              {asset.last_patch_date 
                ? new Date(asset.last_patch_date).toLocaleDateString() 
                : 'Not patched'}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Antivirus Updated:</span>
            <span className={`detail-value ${asset.antivirus_updated_date ? 'text-success' : 'text-warning'}`}>
              {asset.antivirus_updated_date 
                ? new Date(asset.antivirus_updated_date).toLocaleDateString()
                : 'Not updated'}
            </span>
          </div>
        </div>

        <div className="card detail-card">
          <h3>Assignment</h3>
          <div className="detail-row">
            <span className="detail-label">Assigned To:</span>
            <span className="detail-value">
              {asset.assigned_to_name || 'Not assigned'}
            </span>
          </div>
          {asset.assigned_to && (
            <>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{asset.assigned_to_email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Assigned Date:</span>
                <span className="detail-value">
                  {asset.assigned_date 
                    ? new Date(asset.assigned_date).toLocaleDateString() 
                    : 'N/A'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {asset.notes && (
        <div className="card">
          <h3>Notes</h3>
          <p>{asset.notes}</p>
        </div>
      )}
    </div>
  );
}

export default AssetDetail;
